import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Lesson } from '../types/lesson';
import type { Order } from '../types/order';
import type { UserProfile } from '../types/user';
import { useLessons } from '../contexts/LessonContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { DETAIL_COLUMN, DetailTitleRow } from './detailLayout';
import DetailSection from './DetailSection';
import LessonStatusIcon from './LessonStatusIcon';
import { SlotRow } from './programRow';
import { BTN_PRIMARY_SM, BTN_SECONDARY_SM_ON_PAGE } from './buttonStyles';
import { buildProgramSlots, type SlotState } from '../utils/programSlots';
import { lessonStateCopy } from '../utils/lessonStateCopy';
import { useCoachHasMessaged } from '../hooks/useCoachHasMessaged';

/** The player's rhythm: title -> identity row -> status. Same constant. */
const HEADER_GAP = 16;
import { getUserProfile } from '../services/userProfile';
import { getLatestDraft, saveVideoEditDraft } from '../services/videoEditDraft';
import { getProgressReportByOrderId, saveProgressReport } from '../services/progressReports';
import { sendMessage } from '../services/chat';
import { capName } from '../utils/capName';
import { LessonVideoEditor, type VideoEditorDraftData } from './LessonVideoEditor';
import { formatPrice } from '../utils/formatPrice';
import PlayerProfileModal from './PlayerProfileModal';
import type { OriginalVideo, VideoEditDraft, VideoContext } from '../types/videoEdit';

interface CoachBundleProgressProps {
  order: Order;
  /** All lessons in this order, sorted by lessonNumber */
  lessons: Lesson[];
  onBack?: () => void;
  onMessage?: () => void;
  /** Called when coach clicks "Write Report" — parent navigates to progress report view */
  onWriteReport?: () => void;
  /** Called when coach clicks a delivered lesson's "Rewatch →" button */
  onLessonClick?: (lesson: Lesson) => void;
  /**
   * When true, auto-fires `handleStartLesson()` as soon as the in-progress
   * lesson is available — skipping the "Start Lesson →" click. Used by the
   * conversation banner's `Record lesson breakdown` CTA so one click in
   * chat lands the coach directly inside the editor. Fires at most once
   * per mount.
   */
  autoStartLesson?: boolean;
}

export default function CoachBundleProgress({
  order,
  lessons,
  onBack,
  onMessage,
  onWriteReport,
  onLessonClick,
  autoStartLesson = false,
}: CoachBundleProgressProps) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const {
    submitting,
    markLessonHasDraft,
    submitLessonDraft: contextSubmitLessonDraft,
    getOrCreateOriginalVideo: contextGetOrCreateOriginalVideo,
  } = useLessons();
  const { showSuccess, showError, showInfo } = useToast();

  const [playerProfile, setPlayerProfile] = useState<UserProfile | null>(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [hasProgressReport, setHasProgressReport] = useState(false);
  const [showVideoEditor, setShowVideoEditor] = useState(false);
  const [originalVideo, setOriginalVideo] = useState<OriginalVideo | null>(null);
  const [existingDraft, setExistingDraft] = useState<VideoEditDraft | null>(null);
  const [loadedDraftData, setLoadedDraftData] = useState<VideoEditorDraftData | null>(null);
  // Tracks local "submitted" state: true from submission until Firestore confirms delivery
  const [localSubmitted, setLocalSubmitted] = useState(false);

  const totalLessons = order.totalLessons ?? 4;
  const deliveredLessons = lessons.filter(l => l.status === 'delivered').sort((a, b) => (a.lessonNumber ?? 0) - (b.lessonNumber ?? 0));
  const inProgressLesson = lessons.find(l => l.status === 'in_progress');
  const deliveredCount = deliveredLessons.length;
  const isComplete = deliveredCount === totalLessons;

  // The active lesson for which we'd open the editor
  const activeLesson = inProgressLesson ?? null;

  const lessonContext: VideoContext = useMemo(
    () => ({ type: 'lesson', id: activeLesson?.id ?? '' }),
    [activeLesson?.id]
  );

  const hasDraftEdits = existingDraft && (
    existingDraft.voiceoverNotableSegmentClips.length > 0 ||
    (existingDraft.notes && existingDraft.notes.trim().length > 0)
  );

  useEffect(() => {
    if (lessons[0]?.playerId) {
      getUserProfile(lessons[0].playerId).then(setPlayerProfile).catch(() => {});
    }
  }, [lessons]);

  useEffect(() => {
    if (isComplete && order.id) {
      getProgressReportByOrderId(order.id).then(r => { if (r) setHasProgressReport(true); }).catch(() => {});
    }
  }, [isComplete, order.id]);

  // Pre-load draft for the active lesson
  useEffect(() => {
    if (!currentUser || !activeLesson || activeLesson.status !== 'in_progress' || !activeLesson.playerVideoStoragePath) return;
    let cancelled = false;
    (async () => {
      try {
        const video = await contextGetOrCreateOriginalVideo(activeLesson);
        if (cancelled) return;
        setOriginalVideo(video);
        if (video) {
          const draft = await getLatestDraft(lessonContext, video.id, currentUser.uid);
          if (!cancelled) setExistingDraft(draft);
        }
      } catch (err) {
        console.error('Error preloading draft:', err);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLesson?.id, activeLesson?.status]);

  const handleStartLesson = async () => {
    if (!activeLesson) return;
    setLoadedDraftData(null);
    setShowVideoEditor(true);

    let video = originalVideo;
    let didFetchVideo = false;
    if (!video && activeLesson.playerVideoStoragePath && currentUser) {
      try {
        video = await contextGetOrCreateOriginalVideo(activeLesson);
        setOriginalVideo(video);
        didFetchVideo = true;
      } catch (err) {
        console.error('Error creating original video:', err);
        return;
      }
    }

    let currentDraft = existingDraft;
    // Only re-query getLatestDraft when we also had to fetch the video.
    // When originalVideo was already pre-loaded, the pre-load effect has already
    // called getLatestDraft and stored the result in existingDraft. Re-fetching
    // here (after the editor is open) creates a race: a slow response that arrives
    // after the user has recorded resets voiceoverClips via the initialDraft effect.
    if (didFetchVideo && video && currentUser) {
      try {
        currentDraft = await getLatestDraft(lessonContext, video.id, currentUser.uid);
        if (currentDraft) setExistingDraft(currentDraft);
      } catch {
        currentDraft = existingDraft;
      }
    }

    if (currentDraft) {
      setLoadedDraftData({
        notableSegments: currentDraft.notableSegments ?? [],
        voiceoverNotableSegmentClips: currentDraft.voiceoverNotableSegmentClips ?? [],
        notes: currentDraft.notes,
        audioMuted: currentDraft.audioMuted,
        coachNotes: currentDraft.coachNotes,
        videoRotation: currentDraft.videoRotation,
        attachedDrills: currentDraft.attachedDrills ?? [],
      });
    }
  };

  // Auto-fire handleStartLesson when the parent passes autoStartLesson=true
  // (set by the conversation banner's `Record lesson breakdown` CTA). One
  // shot per mount — the ref prevents re-firing if activeLesson identity
  // changes mid-session, and the parent resets the prop when the host
  // panel closes.
  const autoStartFiredRef = useRef(false);
  useEffect(() => {
    if (!autoStartLesson || autoStartFiredRef.current) return;
    if (!activeLesson || !currentUser) return;
    autoStartFiredRef.current = true;
    handleStartLesson();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStartLesson, activeLesson?.id, currentUser?.uid]);

  // Track whether the editor has ever mounted in this panel session.
  // Gates the entry placeholder so it only paints on the first transition
  // (before the editor has rendered for the first time). On exit —
  // whether the coach cancels or submits — the placeholder must NOT
  // paint, because the optimistic close fires `onMessage?.()` immediately
  // and the panel needs to unmount over the editor's last-painted frame,
  // not over a fresh blank placeholder.
  const hasShownEditorRef = useRef(false);
  useEffect(() => {
    if (showVideoEditor) hasShownEditorRef.current = true;
  }, [showVideoEditor]);

  const handleSaveDraft = async (data: VideoEditorDraftData): Promise<VideoEditorDraftData> => {
    if (!currentUser || !activeLesson) throw new Error('No user or active lesson');
    let video = originalVideo;
    if (!video && activeLesson.playerVideoStoragePath) {
      video = await contextGetOrCreateOriginalVideo(activeLesson);
      setOriginalVideo(video);
    }
    if (!video) throw new Error('Could not create video document');

    const savedDraft = await saveVideoEditDraft({
      userId: currentUser.uid,
      userName: currentUser.displayName || 'Coach',
      context: lessonContext,
      originalVideoId: video.id,
      audioMuted: data.audioMuted,
      notes: data.notes,
      existingDraftId: existingDraft?.id,
      notableSegments: data.notableSegments,
      voiceoverNotableSegmentClips: data.voiceoverNotableSegmentClips,
      coachNotes: data.coachNotes,
      attachedDrills: data.attachedDrills,
      videoRotation: data.videoRotation,
    });
    setExistingDraft(savedDraft);
    markLessonHasDraft(activeLesson.id);

    const storedClipMap = new Map(savedDraft.voiceoverNotableSegmentClips.map((c) => [c.id, c]));
    return {
      ...data,
      voiceoverNotableSegmentClips: data.voiceoverNotableSegmentClips.map((clip) => {
        const stored = storedClipMap.get(clip.id);
        return stored
          ? { ...clip, videoStoragePath: stored.videoStoragePath, videoDuration: stored.videoDuration }
          : clip;
      }),
    };
  };

  // Reset localSubmitted once Firestore confirms the lesson is no longer in_progress
  useEffect(() => {
    if (localSubmitted && !inProgressLesson) {
      setLocalSubmitted(false);
    }
  }, [inProgressLesson, localSubmitted]);

  // The mobile header (z-50) and bottom nav (z-9999) sit outside <main overflow-x-hidden>,
  // which creates a Chrome stacking context that traps LVE's fixed positioning. Hide both
  // while LVE is open so they don't intercept pointer events on the editor toolbar.
  useEffect(() => {
    const nav = document.getElementById('mobile-bottom-nav');
    const header = document.getElementById('mobile-header');
    if (showVideoEditor) {
      nav?.style.setProperty('display', 'none', 'important');
      header?.style.setProperty('display', 'none', 'important');
    } else {
      nav?.style.removeProperty('display');
      header?.style.removeProperty('display');
    }
    return () => {
      nav?.style.removeProperty('display');
      header?.style.removeProperty('display');
    };
  }, [showVideoEditor]);

  const handleSubmitDraft = async (data: VideoEditorDraftData) => {
    if (!currentUser || !activeLesson || submitting) return;
    if (data.voiceoverNotableSegmentClips.length === 0) {
      showError('Please add at least one lesson clip before submitting.');
      return;
    }
    setShowVideoEditor(false);
    setLoadedDraftData(null);
    setLocalSubmitted(true);
    // Auto-start entry path: optimistic close. The coach came directly
    // from the chat banner's "Record lesson breakdown" CTA; firing
    // `onMessage?.()` (the panel-close hook) BEFORE awaiting the submit
    // pipeline returns them to the conversation immediately instead of
    // staring at a dark-navy placeholder for the 2-3s the backend takes
    // (`saveVideoEditDraft` blob uploads + `publishAndCompleteDraft`).
    // The pipeline still runs to completion in the background, and
    // showSuccess/showError below fire from the global toast context
    // which survives the panel unmount.
    if (autoStartLesson) onMessage?.();
    try {
      const result = await contextSubmitLessonDraft(activeLesson, data, originalVideo, existingDraft?.id, showInfo);
      setOriginalVideo(result.originalVideo);
      showSuccess('Lesson published and sent to player');
    } catch (err) {
      console.error('Error submitting lesson:', err);
      setLocalSubmitted(false);
      showError(err instanceof Error ? err.message : 'Failed to submit lesson');
    }
  };

  /* Hoisted above the autoStartLesson placeholder's early return: a hook after
     a conditional return is a rules-of-hooks break, and lint caught it. */
  const coachHasMessaged = useCoachHasMessaged(order.conversationId, order.coachId);

  const handleMessage = onMessage ?? (() => navigate(`/inbox/${order.conversationId}`));

  // Live profile takes priority over the order snapshot so the coach
  // sees the player's current name + photo after a rename.
  const livePlayerName = playerProfile?.playerProfile?.displayName ?? playerProfile?.displayName ?? null;
  const playerName = livePlayerName || order.playerName || 'Player';
  const playerFirstName = playerName.split(' ')[0];
  const playerInitials = playerName.split(' ').filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase();
  const playerAvatarUrl = playerProfile?.playerProfile?.avatarUrl ?? playerProfile?.avatarUrl ?? order.playerAvatarUrl;
  const utr = (playerProfile?.playerProfile as unknown as Record<string, unknown> | undefined)?.utr as number | undefined
    ?? (playerProfile as unknown as Record<string, unknown> | undefined)?.utr as number | undefined;

  const isTransferred = lessons.some(l => l.isTransferred) || !!order.isTransferred;
  const transferNote = lessons.find(l => l.isTransferred)?.transferNote ?? order.transferNote;
  const transferredFromCoachName = lessons.find(l => l.isTransferred)?.transferredFromCoachName ?? order.transferredFromCoachName;
  const payPerLessonFromTransfer = lessons.find(l => l.isTransferred)?.payPerLessonCents;

  // Player goal — from the player's profile (set by player on Account page)
  const playerGoal = (playerProfile?.playerProfile as unknown as Record<string, unknown> | undefined)?.goal as string | undefined
    ?? (playerProfile as unknown as Record<string, unknown> | undefined)?.goal as string | undefined;

  // Milestone banner text (used only when there is no in-progress lesson)
  // let milestoneText: string;
  // let milestoneSub: string;
  // if (isComplete) {
  //   milestoneText = `All ${totalLessons} lessons delivered — program complete!`;
  //   milestoneSub = hasProgressReport
  //     ? 'Progress report already submitted.'
  //     : `Write ${playerFirstName}'s progress report to wrap up the program.`;
  // } else if (deliveredCount === 0) {
  //   milestoneText = `Awaiting ${playerFirstName}'s first video`;
  //   milestoneSub = "You'll be notified when the player uploads their video.";
  // } else {
  //   milestoneText = isMobile
  //     ? `Lesson ${deliveredCount} delivered · ${Math.round((deliveredCount / totalLessons) * 100)}%`
  //     : `Lesson ${deliveredCount} delivered — ${Math.round((deliveredCount / totalLessons) * 100)}% complete`;
  //   milestoneSub = `Awaiting ${playerFirstName}'s video for lesson ${deliveredCount + 1}.`;
  // }


  // Auto-start entry path: the coach came directly from the conversation
  // banner's "Record lesson breakdown" CTA. Suppress the bundle progress
  // UI (the "Lesson N is ready for your review / Start Lesson →" card and
  // everything around it) until `showVideoEditor` flips and the editor
  // mounts. The auto-fire useEffect above still runs — effects don't
  // depend on what we render — so `handleStartLesson()` resolves and
  // `setShowVideoEditor(true)` lands a tick later. A full-bleed placeholder
  // with the editor's background color holds the panel layout steady so
  // there's no white flash or layout pop while the editor loads its video
  // and draft. Once `showVideoEditor` is true the existing
  // `LessonVideoEditor` overlay (rendered below) takes over.
  //
  // Crucially, `hasShownEditorRef` keeps the placeholder dormant for the
  // rest of the mount once the editor has rendered at least once. On exit
  // we fire `onMessage?.()` optimistically (panel close) before the async
  // submit pipeline resolves; without the ref guard, `setShowVideoEditor(false)`
  // would reactivate this placeholder and paint a 2-3s navy screen for the
  // duration of the backend round-trip.
  if (autoStartLesson && !showVideoEditor && !hasShownEditorRef.current) {
    return (
      <div className="fixed inset-0 z-modal" style={{ background: 'var(--editor-bg, #0F1A2E)' }} />
    );
  }

  /* Slots, the same model the player's screen uses, so both sides read one
     program the same way. */
  const slots = buildProgramSlots({
    order,
    lessons,
    hasBlockingUpload: false,
    pendingFor: () => null,
    pendingUnattached: null,
  });

  /* The state that leads, in the order it matters to a coach: their own turn
     first, then whatever they are waiting on. */
  const PRIORITY: SlotState[] = [
    'refunded', 'awaiting_review', 'uploading', 'processing', 'ready_to_submit',
  ];
  const leading = PRIORITY.map((st) => slots.find((sl) => sl.state === st)).find(Boolean)
    ?? (slots.length > 0 && slots.every((sl) => sl.state === 'complete' || sl.state === 'ready_to_watch')
      ? slots[slots.length - 1] : undefined);
  const status = leading
    ? lessonStateCopy(leading.state, {
      viewer: 'coach',
      coachFirstName: '',
      playerFirstName,
      lessonNumber: leading.number,
      totalLessons: slots.length,
    })
    : null;

  const showMessageNudge = leading?.state === 'awaiting_review' && coachHasMessaged === false;

  const allDelivered = slots.length > 0
    && slots.every((sl) => sl.state === 'complete' || sl.state === 'ready_to_watch');

  return (
    <div className="flex flex-col h-full overflow-y-auto bg-db-bg">
      <div className={`py-6 md:py-8 pt-5 md:pt-8 ${DETAIL_COLUMN}`}>

        <DetailTitleRow title="Training Program" onBack={onBack} backFallback="/orders" />

        {/* Player identity, on the player's own recipe: 48 avatar, name, then
            the Message pill. The transfer badge and the parent suffix travel
            with the name because both change who this player is. */}
        <div className="flex flex-row items-center justify-between gap-3" style={{ marginTop: HEADER_GAP }}>
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              title={playerName}
              onClick={() => setShowProfileModal(true)}
              className="w-12 h-12 rounded-full flex items-center justify-center overflow-hidden text-[16px] font-bold text-white flex-shrink-0 border-none p-0 cursor-pointer"
              style={{ background: '#54C3EA' }}
            >
              {playerAvatarUrl
                ? <img src={playerAvatarUrl} alt={playerName} className="w-full h-full object-cover" />
                : playerInitials}
            </button>
            <div className="min-w-0 flex flex-col md:flex-row md:items-center md:gap-x-3">
              <button
                type="button"
                onClick={() => setShowProfileModal(true)}
                aria-label={`View ${playerName}'s profile`}
                className="text-[14px] leading-[20px] text-[#1C1C1C] truncate text-left bg-transparent border-none p-0 cursor-pointer hover:opacity-90 transition-opacity"
              >
                {playerName}
                {order.playerAccountType === 'parent' && order.playerParentFirstName && (
                  <span className="ml-1.5 text-[13px] text-[rgba(60,60,67,0.6)]">
                    (Parent: {order.playerParentFirstName})
                  </span>
                )}
              </button>
              {utr != null && (
                <span className="text-[14px] leading-[18px] md:leading-[20px] text-db-text-2 whitespace-nowrap">
                  <span className="hidden md:inline">· </span>UTR {utr}
                </span>
              )}
            </div>
          </div>

          <button
            data-testid="message-player-btn"
            onClick={handleMessage}
            className={`${BTN_SECONDARY_SM_ON_PAGE} flex-shrink-0`}
          >
            <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Message {playerFirstName}
          </button>
        </div>

        {/* Status, the player's block with the coach's words. */}
        {status && (
          <div style={{ marginTop: HEADER_GAP }}>
            <div className="flex items-center" style={{ gap: 8 }}>
              <LessonStatusIcon label={status.label} />
              <span className="text-[13px] leading-[18px] font-semibold text-[#1C1C1C]">{status.label}</span>
            </div>
            <p className="md:hidden text-[13px] leading-[18px] text-[rgba(60,60,67,0.6)]" style={{ marginTop: 6 }}>
              {status.short}
            </p>
            <p className="hidden md:block max-w-[520px] text-[13px] text-db-text-2" style={{ marginTop: 6 }}>
              {status.long}
            </p>
          </div>
        )}

        {/* One line, no control of its own. The Message button in the identity
            row above is the action — a link here would be the second entry
            point to messaging that this was not allowed to add. It appears only
            while a lesson is awaiting the coach's review AND the thread has no
            note from them, so it goes away by being acted on rather than by
            being dismissed. */}
        {showMessageNudge && (
          <p className="text-[13px] leading-[18px] text-db-text-2" style={{ marginTop: 6 }}>
            {playerFirstName} has not heard from you yet. Let them know when to expect the breakdown.
          </p>
        )}


        {/* The transfer note stays, as a section rather than a tinted banner:
            it is information about this player, and it reads once. */}
        {isTransferred && transferNote && (
          <DetailSection className="mt-8" header="Transferred to you">
            <p className="text-[15px] leading-[20px] text-[#1C1C1C] whitespace-pre-wrap">{transferNote}</p>
            {transferredFromCoachName && (
              <p className="text-[13px] leading-[18px] text-[rgba(60,60,67,0.6)] mt-2">
                Previously with {transferredFromCoachName}
              </p>
            )}
            {payPerLessonFromTransfer != null && payPerLessonFromTransfer > 0 && (
              <p className="text-[13px] leading-[18px] text-[rgba(60,60,67,0.6)] mt-2">
                {formatPrice(payPerLessonFromTransfer)}/lesson
              </p>
            )}
          </DetailSection>
        )}

        {/* The player's goal, on the same DetailSection every note on this side
            uses — the tinted callout with its left border was the only element
            of its kind in the app. */}
        {playerGoal && (
          <DetailSection className="mt-8" header={`${playerFirstName}'s goal`}>
            <p data-testid="player-goal-text" className="text-[15px] leading-[20px] text-[#1C1C1C] whitespace-pre-wrap">
              {playerGoal}
            </p>
          </DetailSection>
        )}

        <div
          className="text-[13px] leading-[18px] font-semibold uppercase text-[rgba(60,60,67,0.6)]"
          style={{ letterSpacing: '0.5px', marginTop: 32, marginBottom: 16 }}
        >
          Lessons
        </div>
        <div style={{ background: '#FFFFFF', borderRadius: 12, overflow: 'hidden' }}>
          {slots.map((slot, i) => (
            <SlotRow
              key={slot.number}
              slot={slot}
              first={i === 0}
              label={lessonStateCopy(slot.state, {
                viewer: 'coach', coachFirstName: '', playerFirstName,
                lessonNumber: slot.number, totalLessons: slots.length,
              })?.label}
              /* Delivered opens the rewatch; nothing else has a screen. */
              onOpen={slot.lesson && (slot.state === 'ready_to_watch' || slot.state === 'complete')
                ? () => onLessonClick?.(slot.lesson!)
                : undefined}
              /* Start Lesson lives on the row it acts on, not in a banner above
                 the list. It is the coach's equivalent of the player's Upload,
                 so it sits in the same slot on the same row recipe. */
              action={slot.state === 'awaiting_review'
                ? {
                  label: localSubmitted ? 'Submitted' : (hasDraftEdits ? 'Edit' : 'Start'),
                  onClick: handleStartLesson,
                  disabled: submitting || localSubmitted,
                  testId: 'start-lesson-btn',
                }
                : undefined}
            />
          ))}
        </div>

        {allDelivered && onWriteReport && (
          <DetailSection className="mt-8">
            <div className="flex items-center justify-between gap-3">
              <span className="text-[15px] leading-[20px] text-[#1C1C1C]">
                {hasProgressReport ? 'Progress report submitted' : 'Progress report'}
              </span>
              {!hasProgressReport && (
                <button
                  data-testid="write-report-btn"
                  onClick={onWriteReport}
                  className={`${BTN_PRIMARY_SM} shrink-0`}
                >
                  Write Report
                </button>
              )}
            </div>
          </DetailSection>
        )}

      </div>

      {/* Full-screen video editor overlay */}
      {showVideoEditor && activeLesson?.playerVideoUrl && (() => {
        // Final program lesson = training_program AND lessonNumber === totalLessons.
        // Primary check: lessonNumber field on the lesson doc. Fallback: if lessonNumber
        // is missing (can happen on staging when the CF hasn't set it yet), use the
        // delivered count — when deliveredCount === totalLessons - 1 this must be the
        // last lesson remaining.
        const isFinalProgramLesson =
          order.type === 'training_program' &&
          (
            (activeLesson.lessonNumber ?? 0) === totalLessons ||
            (activeLesson.lessonNumber === undefined && deliveredCount === totalLessons - 1)
          );

        return (
          <LessonVideoEditor
            lessonRequest={{ ...activeLesson, playerVideoUrl: activeLesson.playerVideoUrl }}
            onCancel={() => {
              setShowVideoEditor(false);
              setLoadedDraftData(null);
              // Auto-start entry: cancelling the editor returns the coach to
              // the chat (the surface they actually came from), not the
              // intermediate bundle progress UI.
              if (autoStartLesson) onMessage?.();
            }}
            onSaveDraft={handleSaveDraft}
            onSubmitDraft={handleSubmitDraft}
            initialDraft={loadedDraftData || undefined}
            playerId={activeLesson.playerId}
            lessonId={activeLesson.id}
            originalVideoId={originalVideo?.id}
            isFinalProgramLesson={isFinalProgramLesson}
            playerFirstName={playerFirstName}
            playerName={playerName}
            playerAvatarUrl={playerAvatarUrl}
            playerUtr={utr}
            totalLessons={totalLessons}
            playerGoal={playerGoal}
            // Final-lesson submit: lesson submit FIRST (so a report-write
            // failure can't orphan a draft), then progress-report write.
            // Throws on lesson-side failure so the modal stays open for
            // retry. On lesson success regardless of report success, resolves
            // — the modal closes and the toast surfaces here.
            onFinalSubmit={async ({ draftData, report }) => {
              if (!currentUser || !activeLesson) {
                throw new Error('No user or active lesson');
              }
              if (draftData.voiceoverNotableSegmentClips.length === 0) {
                showError('Please add at least one lesson clip before submitting.');
                throw new Error('No clips');
              }
              // Step 1: lesson submit pipeline (same path the non-final flow uses).
              setLocalSubmitted(true);
              let lessonSubmitOk = false;
              try {
                const result = await contextSubmitLessonDraft(
                  activeLesson, draftData, originalVideo, existingDraft?.id, showInfo,
                );
                setOriginalVideo(result.originalVideo);
                lessonSubmitOk = true;
              } catch (err) {
                console.error('Error submitting final lesson:', err);
                setLocalSubmitted(false);
                showError(err instanceof Error ? err.message : 'Failed to submit lesson');
                throw err;  // keep the modal open for retry
              }

              // Step 2: progress report write. If this fails, the lesson is
              // already committed — surface the partial-failure toast and
              // continue (don't throw — modal should close on lesson success).
              // Capture the returned doc ID so step 3 can reference it on
              // the chat message.
              let savedReportId: string | null = null;
              if (lessonSubmitOk) {
                try {
                  const savedReport = await saveProgressReport({
                    coachId: currentUser.uid,
                    playerId: activeLesson.playerId,
                    orderId: order.id,
                    lessonId: activeLesson.id,
                    whatImproved: report.whatImproved,
                    keepDoing: report.keepDoing,
                    nextFocus: report.nextFocus,
                    personalNote: report.personalNote || null,
                  });
                  savedReportId = savedReport.id;
                  showSuccess(`Lesson and report sent to ${capName(playerFirstName)}.`);
                } catch (reportErr) {
                  console.error('Progress report write failed:', reportErr);
                  showError("Lesson submitted, but we couldn't save the report. Try again from your delivered orders.");
                }
              }

              // Step 3: write the conversation message that surfaces the
              // report card in the thread (below the existing breakdown-
              // sent card). Best-effort — if it fails the report is still
              // reachable via /orders?tab=delivered, so we just log and
              // continue rather than alarming the coach.
              if (lessonSubmitOk && savedReportId) {
                try {
                  await sendMessage(
                    activeLesson.conversationId,
                    currentUser.uid,
                    'Progress Report',
                    'progress_report_delivered',
                    undefined,
                    activeLesson.id,
                    undefined,
                    savedReportId,
                  );
                } catch (msgErr) {
                  console.warn('Progress-report chat message write failed:', msgErr);
                }
              }

              // Step 3: same UI cleanup the non-final submit performs —
              // close the editor and (in auto-start mode) close the host
              // panel so the coach lands back on the conversation.
              setShowVideoEditor(false);
              setLoadedDraftData(null);
              if (autoStartLesson) onMessage?.();
            }}
          />
        );
      })()}
      {showProfileModal && (
        <PlayerProfileModal
          playerId={order.playerId}
          seedName={playerName}
          seedAvatarUrl={playerAvatarUrl}
          onClose={() => setShowProfileModal(false)}
        />
      )}
    </div>
  );
}
